/**
 * Tenant-scoped alerts API.
 * Always pass tenantId explicitly - never rely on entity layer or localStorage for tenant scoping.
 */

const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'https://api.neogtm.com'
  : 'https://api.neogtm.com';

export const alertsApi = {
  async list(userId, tenantId, limit = 50) {
    const where = JSON.stringify({ user_id: userId, tenant_id: tenantId });
    const response = await fetch(
      `${API_BASE_URL}/api/entities/Alert?where=${encodeURIComponent(where)}&order=-created_at&limit=${limit}`,
      {
        headers: { 'x-tenant-id': tenantId },
        credentials: 'include'
      }
    );
    if (!response.ok) throw new Error(`Failed to fetch alerts: ${response.status}`);
    return response.json();
  },

  async update(alertId, updates, tenantId) {
    const response = await fetch(`${API_BASE_URL}/api/entities/Alert/${alertId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-id': tenantId
      },
      credentials: 'include',
      body: JSON.stringify(updates)
    });
    if (!response.ok) throw new Error(`Failed to update alert: ${response.status}`);
    return response.json();
  },

  async markAllRead(alerts, tenantId) {
    const now = new Date().toISOString();
    const unread = alerts.filter(a => !a.read_at);
    if (unread.length === 0) return { success: true, updated: 0, total: 0 };

    const BATCH_SIZE = 20;
    const chunks = [];
    for (let i = 0; i < unread.length; i += BATCH_SIZE) {
      chunks.push(unread.slice(i, i + BATCH_SIZE));
    }

    let updated = 0;
    let failed = 0;

    for (const chunk of chunks) {
      const results = await Promise.allSettled(
        chunk.map(async (alert) => {
          const response = await fetch(`${API_BASE_URL}/api/entities/Alert/${alert.id}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'x-tenant-id': tenantId
            },
            credentials: 'include',
            body: JSON.stringify({ read_at: now })
          });
          if (!response.ok) {
            throw new Error(`Failed to update alert ${alert.id}: ${response.status}`);
          }
          return alert.id;
        })
      );

      updated += results.filter((r) => r.status === 'fulfilled').length;
      failed += results.filter((r) => r.status === 'rejected').length;
    }

    if (failed > 0) {
      console.warn('Some alerts failed to mark as read:', failed);
    }

    return { success: failed === 0, updated, total: unread.length };
  },

  async delete(alertId, tenantId) {
    const response = await fetch(`${API_BASE_URL}/api/entities/Alert/${alertId}`, {
      method: 'DELETE',
      headers: { 'x-tenant-id': tenantId },
      credentials: 'include'
    });
    if (!response.ok) throw new Error(`Failed to delete alert: ${response.status}`);
    return response.json().catch(() => ({}));
  },

  async markAllSeen(alerts, tenantId) {
    const now = new Date().toISOString();
    const unseen = alerts.filter(a => !a.seen_at);

    const results = await Promise.allSettled(
      unseen.map(alert =>
        fetch(`${API_BASE_URL}/api/entities/Alert/${alert.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'x-tenant-id': tenantId
          },
          credentials: 'include',
          body: JSON.stringify({ seen_at: now })
        })
      )
    );

    return { success: true, updated: unseen.length };
  }
};
